import React, {FC, useEffect, useState} from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import {Auth, UnAuthed} from "@irati/auth/types"
import ObserveWithPriority from "@irati/auth/helpers/auth/ObserveWithPriority"
import AuthStream from "@irati/auth/streams/auth"
import adminTracking from "@irati-tracking/admin"
import UnauthedStream from "@irati/auth/streams/unauthed"
import {
  ADMIN_NAVIGATION_SIDEBAR_WIDTH,
  NAVIGATION_SIDEBAR_WIDTH,
  COLLAPSED_SIDEBAR_WIDTH,
  SECONDARY_NAV_COLLAPSED_SIDEBAR_WIDTH} from "@irati/ui/src/AppContainer/styled"
import shouldSecondNavigationRender from "@irati/ui/src/AppContainer/shouldSecondNavigationRender"
import Flex from "@irati/ui/src/Flex"

import NavigationMenuContent from "../Header/shared/NavigationMenuContent"
import AdminNavigationMenuContent from "../Header/shared/AdminNavigationMenuContent"
import AdminSecondaryNavigationMenuContent from "../Header/shared/AdminSecondaryNavigationMenuContent"

const StyledAppNavigation = styled.div`
  position: fixed;
  top: 0;
  ${({leftPosition}) => leftPosition && `left: ${leftPosition}px;`}
  z-index: 100;
  background-color: ${({theme}) => theme.colors.utility.white};
  height: 100vh;
  width: ${({isCollapsed, isSecondaryNav, isAdmin}) => {
    if (isCollapsed) {
      if(isSecondaryNav) {
        return SECONDARY_NAV_COLLAPSED_SIDEBAR_WIDTH
      }
      return COLLAPSED_SIDEBAR_WIDTH
    }
    if (isAdmin) {
      return ADMIN_NAVIGATION_SIDEBAR_WIDTH
    }
    return NAVIGATION_SIDEBAR_WIDTH
  }}px;
  border-right: 1px solid ${({theme}) => theme.colors.greys.mediumLight};
  overflow-y: auto;
`

interface Props {
  shouldDisplay: boolean,
  onUpdateNavigationStates: () => void,
  collapsedNavs: {
    firstNav: boolean,
    secondNav: boolean
  }
}

interface Context {
  apps: {[key: string]: string}
}

const AppNavigation: FC<Props> = ({
  shouldDisplay,
  onUpdateNavigationStates,
  collapsedNavs}, context: Context) => {
  const [auth, setAuth] = useState<Partial<Auth | UnAuthed> | undefined>(undefined)
  const [isFirstNavCollapsed, setIsFirstNavCollapsed] = useState<Boolean>(false)
  const [isSecondNavCollapsed, setIsSecondNavCollapsed] = useState<Boolean>(false)
  const {appPath, apps: contextApps} = context
  const {ADMIN} = contextApps
  const isAdmin = appPath === ADMIN

  useEffect(() => {
    const stream = new ObserveWithPriority<Auth | UnAuthed>({streams: [AuthStream, UnauthedStream], key: "branding"})
      .subscribe((authResponse) => {
        if (authResponse && authResponse.branding) {
          setAuth(authResponse)
        }
      })

    return () => {
      stream.unsubscribe()
    }
  }, [])

  if (!shouldDisplay) {
    return null
  }

  const handleFirstMenuClick = () => {
    adminTracking.adminNavMainMenuExpanseCollapse()
    onUpdateNavigationStates({firstNav: !collapsedNavs.firstNav, secondNav: collapsedNavs.secondNav})
    setIsFirstNavCollapsed(!isFirstNavCollapsed)
  }

  const handleSecondMenuClick = () => {
    adminTracking.adminNavSubMenuExpanseCollapse()
    onUpdateNavigationStates({firstNav: collapsedNavs.firstNav, secondNav: !collapsedNavs.secondNav})
    setIsSecondNavCollapsed(!isSecondNavCollapsed)
  }

  return (
    isAdmin
      ? <Flex>
        <StyledAppNavigation isCollapsed={collapsedNavs.firstNav} isAdmin>
          <AdminNavigationMenuContent
            onClick={handleFirstMenuClick}
            isCollapsed={collapsedNavs.firstNav}
            businessLogoUrl={auth?.branding?.businessLogoUrl} />
        </StyledAppNavigation>
        {shouldSecondNavigationRender() &&
          <StyledAppNavigation
            isSecondaryNav
            isCollapsed={collapsedNavs.secondNav}
            leftPosition={collapsedNavs.firstNav ? COLLAPSED_SIDEBAR_WIDTH : ADMIN_NAVIGATION_SIDEBAR_WIDTH}>
            <AdminSecondaryNavigationMenuContent
              onClick={handleSecondMenuClick}
              isCollapsed={collapsedNavs.secondNav}
              businessLogoUrl={auth?.branding?.businessLogoUrl} />
          </StyledAppNavigation>
        }
      </Flex>
      : <StyledAppNavigation>
        <NavigationMenuContent isSidebarNavigation businessLogoUrl={auth?.branding?.businessLogoUrl} />
      </StyledAppNavigation>
  )
}

AppNavigation.contextTypes = {
  apps: PropTypes.object.isRequired,
  appPath: PropTypes.string.isRequired
}

export default AppNavigation
